// src/pages/Auth/Login/Login.jsx
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, easeOut, AnimatePresence } from "framer-motion";
import Classes from "../Signup/Auth.module.css";
import { EyeCloseIcon, EyeOpenIcon } from "../../../../utils/Icons";
import { authActions } from "../../../../redux-store";

const LoadingSpinner = () => (
  <div className="loadingio-spinner-eclipse-nq4q5u6dq7r">
    <div className="ldio-x2uulkbinbj">
      <div></div>
    </div>
  </div>
);

export default function Login() {
  // pages: 'signin' | 'forgot'
  const [state, setState] = useState("signin");

  // signin fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);

  // forgot fields
  const [resetEmail, setResetEmail] = useState("");

  // ui
  const [loading, setLoading] = useState(false);
  const [incorrectError, setIncorrectError] = useState(false);
  const [showEye, setShowEye] = useState(false);
  const [userAlreadyExistError] = useState(false); // reserved (kept for style parity)
  const [codeSentMsg, setCodeSentMsg] = useState(false);
  const [invalidEmailError, setInvalidEmailError] = useState(false);

  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const location = useLocation();

  // animation presets
  const wholeFormInitial = { y: -16, opacity: 0 };
  const wholeFormAnimate = {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: easeOut, delay: 0.15 },
  };

  // Prefill remembered email
  useEffect(() => {
    const remembered = localStorage.getItem("remember_email");
    if (remembered) {
      setEmail(remembered);
      setRemember(true);
    }
  }, []);

  /* ───────── Sign In ───────── */
  const loginSubmitHandler = async () => {
    setIncorrectError(false);
    setLoading(true);
    try {
      const ok = await dispatch(authActions.login(email.trim(), password));
      if (ok) {
        // remember email if checked
        if (remember) localStorage.setItem("remember_email", email.trim());
        else localStorage.removeItem("remember_email");

        setLoading(false);
        const redirectTo = location.state?.from?.pathname || "/";
        navigateTo(redirectTo);
      } else {
        setLoading(false);
        setIncorrectError(true);
      }
    } catch (e) {
      setLoading(false);
      setIncorrectError(true);
    }
  };

  const handleSignInSubmit = (e) => {
    e.preventDefault();
    loginSubmitHandler();
  };

  /* ───────── Forgot Password ───────── */
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setInvalidEmailError(false);
    setCodeSentMsg(false);

    try {
      // Pass a base URL; backend usually appends its reset token.
      const resetLinkBase = `${window.location.origin}/createpassword/`;
      const ok = await dispatch(
        authActions.forgotPassword(resetEmail.trim(), resetLinkBase)
      );

      if (ok) {
        setLoading(false);
        setCodeSentMsg(true);
        setInvalidEmailError(false);
        // auto-hide success after some time
        setTimeout(() => setCodeSentMsg(false), 10000);
      } else {
        setLoading(false);
        setInvalidEmailError(true);
        setTimeout(() => setInvalidEmailError(false), 10000);
      }
    } catch (err) {
      setLoading(false);
      setInvalidEmailError(true);
      setTimeout(() => setInvalidEmailError(false), 10000);
    }
  };

  return (
    <>
      <div className={Classes.navContainer}>
        <div></div>
        <a href="/" className={Classes.miniLogo2} style={{ textDecoration: "none" }}>
          <div className="logo">
            <img src="/assets/images/logo.webp" alt="logo" />
          </div>
        </a>
        <div></div>
      </div>

      <div className={Classes.signUp}>
        <AnimatePresence mode="wait">
          {/* ───────── Sign In Page ───────── */}
          {state === "signin" && (
            <motion.div
              key="signin"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.5, ease: easeOut, delay: 0.1 }}
            >
              <motion.form
                onSubmit={handleSignInSubmit}
                className={Classes.signUpInnerCon}
                initial={wholeFormInitial}
                animate={wholeFormAnimate}
              >
                <h5 className={Classes.heading2}>Welcome Back to Fuel Clubs</h5>
                <p className={Classes.subtitle}>
                  Sign in today to continue to your dashboard
                </p>

                {/* Email */}
                <input
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  type="email"
                  placeholder="Enter your email"
                  required
                  className={`${Classes.inputField} ${Classes.mb20} ${
                    incorrectError || userAlreadyExistError ? Classes.inputError : ""
                  }`}
                />

                {/* Password */}
                <div
                  className={`${Classes.passwordCon} ${Classes.mb20} ${
                    incorrectError ? Classes.inputError : ""
                  }`}
                >
                  <input
                    onChange={(e) => setPassword(e.target.value)}
                    type={showEye ? "text" : "password"}
                    placeholder="Enter your password"
                    required
                  />
                  {showEye ? (
                    <EyeOpenIcon onClick={() => setShowEye((s) => !s)} />
                  ) : (
                    <EyeCloseIcon onClick={() => setShowEye((s) => !s)} />
                  )}
                </div>

                {/* Remember + Forgot row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      color: "#11111187",
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 400,
                      userSelect: "none",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    Remember Me
                  </label>

                  <button
                    type="button"
                    className={Classes.linkButton}
                    onClick={() => {
                      setState("forgot");
                      setResetEmail(email || "");
                      setIncorrectError(false);
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>

                {incorrectError && (
                  <p className={Classes.error} style={{ marginBottom: 8 }}>
                    Invalid email or password. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  className={`${Classes.button} ${Classes.signInButton}`}
                  disabled={loading}
                >
                  Sign in
                  {loading && <LoadingSpinner />}
                </button>
              </motion.form>
            </motion.div>
          )}

          {/* ───────── Forgot Password Page ───────── */}
          {state === "forgot" && (
            <motion.div
              key="forgot"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.5, ease: easeOut, delay: 0.1 }}
            >
              <motion.form
                onSubmit={handleForgotSubmit}
                className={Classes.signUpInnerCon}
                initial={wholeFormInitial}
                animate={wholeFormAnimate}
              >
                <h5 className={Classes.heading2}>Forgot Your Password?</h5>
                <p className={Classes.subtitle}>
                  Enter your email and we will send instructions to reset your password
                </p>

                <input
                  onChange={(e) => setResetEmail(e.target.value)}
                  value={resetEmail}
                  type="email"
                  placeholder="Enter your email"
                  required
                  className={`${Classes.inputField} ${Classes.mb20}`}
                />

                {invalidEmailError && (
                  <p className={Classes.error} style={{ marginTop: -6, marginBottom: 10 }}>
                    We couldn't find an account with that email.
                  </p>
                )}

                <button
                  type="submit"
                  className={`${Classes.button} ${Classes.signInButton}`}
                  disabled={loading}
                >
                  Continue
                  {loading && <LoadingSpinner />}
                </button>

                <p className={Classes.signupLink2}>
                  <button
                    type="button"
                    onClick={() => setState("signin")}
                    className={Classes.linkButton}
                  >
                    Back to sign in page
                  </button>
                </p>

                {codeSentMsg && (
                  <p className={Classes.success} style={{ textAlign: "center" }}>
                    Reset instructions sent to your email.
                  </p>
                )}
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
